import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  ArrowLeft, Users, CheckCircle, XCircle, Trash2, Mail, Clock, Shield, RefreshCw,
  Rss, Search, Settings, Plus, ExternalLink, Send, Eye,
  LayoutDashboard, FolderKanban, Megaphone, Inbox, Gavel, HardHat, Briefcase, BarChart3, Package, Calculator
} from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import { toast } from 'sonner';
import CRMDashboard from './crm/CRMDashboard';
import CRMContacts from './crm/CRMContacts';
import CRMProjects from './crm/CRMProjects';
import CRMCampaigns from './crm/CRMCampaigns';
import CRMCampaignAnalytics from './crm/CRMCampaignAnalytics';
import CRMEmailSync from './crm/CRMEmailSync';
import CRMBids from './crm/CRMBids';
import CRMSubcontractors from './crm/CRMSubcontractors';
import CRMInventory from './crm/CRMInventory';
import CRMBelzona from './crm/CRMBelzona';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = 'jasonkraft@bcs-us.com';

// ─── Shared fetch helper ────────────────────────────────────────
const apiFetch = async (path, opts = {}) => {
  const savedToken = localStorage.getItem('bcs_session_token');
  const headers = { ...(opts.headers || {}) };
  if (savedToken) headers['Authorization'] = `Bearer ${savedToken}`;
  const res = await fetch(`${API_URL}${path}`, { credentials: 'include', ...opts, headers });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Request failed');
  }
  return res.json();
};

// ═══════════════════════════════════════════════════════════════
// Tab: Users
// ═══════════════════════════════════════════════════════════════
const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    try { setUsers(await apiFetch('/api/admin/users')); }
    catch { toast.error('Failed to load users'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleAction = async (action, userId, email) => {
    if (action === 'delete' && !window.confirm(`Delete ${email}? This cannot be undone.`)) return;
    setActionLoading(userId);
    try {
      const map = {
        approve: () => apiFetch(`/api/admin/users/${userId}/approve`, { method: 'POST' }),
        revoke: () => apiFetch(`/api/admin/users/${userId}/revoke`, { method: 'POST' }),
        delete: () => apiFetch(`/api/admin/users/${userId}`, { method: 'DELETE' }),
      };
      await map[action]();
      toast.success(action === 'approve' ? 'User approved' : action === 'revoke' ? 'Access revoked' : 'User deleted');
      await loadUsers();
    } catch { toast.error(`Failed to ${action} user`); }
    finally { setActionLoading(null); }
  };

  const pending = users.filter(u => !u.is_approved);
  const approved = users.filter(u => u.is_approved);

  const UserRow = ({ user, isPending }) => (
    <div data-testid={`user-row-${user.user_id}`} className={`flex items-center justify-between p-4 rounded-lg border ${isPending ? 'bg-yellow-50 border-yellow-200' : 'bg-slate-50 border-slate-200'}`}>
      <div className="flex items-center gap-4">
        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
          <Users size={20} className="text-gray-500" />
        </div>
        <div>
          <p className="font-semibold text-slate-900">{user.name}</p>
          <p className="text-sm text-gray-600">{user.email}</p>
          <p className="text-xs text-gray-400">{isPending ? 'Signed up' : 'Approved'}: {new Date(isPending ? user.created_at : (user.approved_at || user.created_at)).toLocaleDateString()}</p>
        </div>
      </div>
      <div className="flex gap-2">
        {isPending ? (
          <Button data-testid={`approve-btn-${user.user_id}`} size="sm" className="bg-green-600 hover:bg-green-700" onClick={() => handleAction('approve', user.user_id)} disabled={actionLoading === user.user_id}>
            <CheckCircle size={16} className="mr-1" /> Approve
          </Button>
        ) : (
          <Button data-testid={`revoke-btn-${user.user_id}`} size="sm" variant="outline" onClick={() => handleAction('revoke', user.user_id)} disabled={actionLoading === user.user_id}>
            <XCircle size={16} className="mr-1" /> Revoke
          </Button>
        )}
        <Button data-testid={`delete-btn-${user.user_id}`} size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleAction('delete', user.user_id, user.email)} disabled={actionLoading === user.user_id}>
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[
          { icon: Users, color: 'blue', label: 'Total Users', val: users.length },
          { icon: Clock, color: 'yellow', label: 'Pending', val: pending.length },
          { icon: CheckCircle, color: 'green', label: 'Approved', val: approved.length },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className={`p-3 bg-${s.color}-100 rounded-full`}><s.icon className={`text-${s.color}-700`} size={24} /></div>
                <div><p className="text-2xl font-bold text-slate-900">{s.val}</p><p className="text-sm text-gray-600">{s.label}</p></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pending */}
      <Card className="border-2 border-yellow-200">
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="flex items-center gap-2"><Clock className="text-yellow-600" size={22} /> Pending Approvals</CardTitle>
              <CardDescription>Users waiting for approval</CardDescription>
            </div>
            <Button data-testid="refresh-users-btn" variant="outline" size="sm" onClick={loadUsers} disabled={loading}>
              <RefreshCw size={16} className={`mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
          : pending.length === 0 ? <div className="text-center py-8 text-gray-500"><CheckCircle className="mx-auto mb-2 text-green-500" size={32} /><p>No pending approvals</p></div>
          : <div className="space-y-3">{pending.map(u => <UserRow key={u.user_id} user={u} isPending />)}</div>}
        </CardContent>
      </Card>

      {/* Approved */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><CheckCircle className="text-green-600" size={22} /> Approved Users</CardTitle>
          <CardDescription>Users with active portal access</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
          : approved.length === 0 ? <div className="text-center py-8 text-gray-500"><p>No approved users yet</p></div>
          : <div className="space-y-3">{approved.map(u => <UserRow key={u.user_id} user={u} />)}</div>}
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Tab: Feed (Solicitations)
// ═══════════════════════════════════════════════════════════════
const FeedTab = () => {
  const [sols, setSols] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setSols(await apiFetch('/api/admin/solicitations')); }
    catch { toast.error('Failed to load solicitations'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const r = await apiFetch('/api/admin/feed/refresh', { method: 'POST' });
      toast.success(r.status === 'success' ? `Found ${r.new_matches} new match(es)` : r.status === 'no_keywords' ? 'Add keywords first' : r.message || 'Feed refreshed');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setRefreshing(false); }
  };

  const handleDelete = async (id) => {
    try { await apiFetch(`/api/admin/solicitations/${id}`, { method: 'DELETE' }); setSols(s => s.filter(x => x.sol_id !== id)); toast.success('Removed'); }
    catch { toast.error('Failed to delete'); }
  };

  const handleMarkRead = async (id) => {
    try { await apiFetch(`/api/admin/solicitations/${id}/read`, { method: 'POST' }); setSols(s => s.map(x => x.sol_id === id ? { ...x, is_read: true } : x)); }
    catch { toast.error('Failed to mark read'); }
  };

  const unread = sols.filter(s => !s.is_read).length;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Matched Solicitations</h3>
          <p className="text-sm text-gray-500">{sols.length} total, {unread} unread</p>
        </div>
        <Button data-testid="refresh-feed-btn" onClick={handleRefresh} disabled={refreshing} className="bg-blue-900 hover:bg-blue-800">
          <RefreshCw size={16} className={`mr-2 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Fetching...' : 'Refresh Feed'}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900 mx-auto" /></div>
      ) : sols.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <Rss className="mx-auto mb-3 text-gray-300" size={40} />
            <p className="font-medium">No matching solicitations yet</p>
            <p className="text-sm mt-1">Add keywords and refresh the feed to start tracking bids.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sols.map(sol => (
            <Card key={sol.sol_id} data-testid={`sol-card-${sol.sol_id}`} className={`transition-colors ${sol.is_read ? 'opacity-70' : 'border-l-4 border-l-blue-600'}`}>
              <CardContent className="py-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {!sol.is_read && <span className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0" />}
                      <a href={sol.link} target="_blank" rel="noopener noreferrer" className="font-semibold text-slate-900 hover:text-blue-700 truncate block">
                        {sol.title} <ExternalLink size={14} className="inline ml-1" />
                      </a>
                    </div>
                    {sol.summary && <p className="text-sm text-gray-500 line-clamp-2 mb-2">{sol.summary}</p>}
                    <div className="flex items-center gap-2 flex-wrap">
                      {sol.matched_keywords?.map(kw => (
                        <Badge key={kw} className="bg-emerald-100 text-emerald-800 text-xs">{kw}</Badge>
                      ))}
                      <span className="text-xs text-gray-400 ml-auto">{sol.pub_date || new Date(sol.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {!sol.is_read && (
                      <Button data-testid={`mark-read-${sol.sol_id}`} size="sm" variant="ghost" className="text-gray-400 hover:text-blue-600" onClick={() => handleMarkRead(sol.sol_id)} title="Mark read">
                        <Eye size={16} />
                      </Button>
                    )}
                    <Button data-testid={`delete-sol-${sol.sol_id}`} size="sm" variant="ghost" className="text-gray-400 hover:text-red-600" onClick={() => handleDelete(sol.sol_id)} title="Remove">
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Tab: Keywords
// ═══════════════════════════════════════════════════════════════
const KeywordsTab = () => {
  const [keywords, setKeywords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newWord, setNewWord] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setKeywords(await apiFetch('/api/admin/keywords')); }
    catch { toast.error('Failed to load keywords'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newWord.trim()) return;
    setAdding(true);
    try {
      await apiFetch('/api/admin/keywords', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ word: newWord.trim() })
      });
      setNewWord('');
      toast.success('Keyword added');
      await load();
    } catch (err) { toast.error(err.message); }
    finally { setAdding(false); }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`/api/admin/keywords/${id}`, { method: 'DELETE' });
      setKeywords(k => k.filter(x => x.keyword_id !== id));
      toast.success('Keyword removed');
    } catch { toast.error('Failed to remove keyword'); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Search size={20} /> Filter Keywords</CardTitle>
          <CardDescription>Solicitations containing these words in the title or description will be captured and emailed to you.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleAdd} className="flex gap-2 mb-6">
            <input
              data-testid="keyword-input"
              type="text"
              value={newWord}
              onChange={e => setNewWord(e.target.value)}
              placeholder="e.g. coating, epoxy, corrosion, tank..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <Button data-testid="add-keyword-btn" type="submit" disabled={adding || !newWord.trim()} className="bg-blue-900 hover:bg-blue-800">
              <Plus size={16} className="mr-1" /> Add
            </Button>
          </form>

          {loading ? (
            <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-900 mx-auto" /></div>
          ) : keywords.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Search className="mx-auto mb-2" size={32} />
              <p>No keywords yet. Add some to start monitoring solicitations.</p>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {keywords.map(kw => (
                <div key={kw.keyword_id} data-testid={`keyword-${kw.keyword_id}`} className="flex items-center gap-1 bg-slate-100 border border-slate-200 rounded-full px-4 py-1.5 text-sm text-slate-700 group hover:border-red-300 transition-colors">
                  <span>{kw.word}</span>
                  <button onClick={() => handleDelete(kw.keyword_id)} className="ml-1 text-gray-400 group-hover:text-red-500 transition-colors" title="Remove">
                    <XCircle size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <p className="text-sm text-gray-500">
            <strong>Tip:</strong> Use broad terms like "coating", "repair", "epoxy", "tank", "pipe", "wastewater" to catch relevant LA County bids. The monitor checks both the title and description of each posting.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Tab: Settings
// ═══════════════════════════════════════════════════════════════
const SettingsTab = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [form, setForm] = useState({ feed_url: '', notify_email: '', is_enabled: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const s = await apiFetch('/api/admin/feed-settings');
      setSettings(s);
      setForm({ feed_url: s.feed_url || '', notify_email: s.notify_email || '', is_enabled: s.is_enabled !== false });
    } catch { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiFetch('/api/admin/feed-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      toast.success('Settings saved');
      await load();
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const r = await apiFetch('/api/admin/test-email', { method: 'POST' });
      toast.success(r.message);
    } catch (e) { toast.error(e.message); }
    finally { setTesting(false); }
  };

  if (loading) return <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900 mx-auto" /></div>;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Settings size={20} /> Feed Monitor Settings</CardTitle>
          <CardDescription>Configure the RSS feed source, email notifications, and schedule.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">RSS Feed URL</label>
            <input
              data-testid="feed-url-input"
              type="url"
              value={form.feed_url}
              onChange={e => setForm(f => ({ ...f, feed_url: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-mono"
              placeholder="https://..."
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700 mb-1 block">Notification Email</label>
            <input
              data-testid="notify-email-input"
              type="email"
              value={form.notify_email}
              onChange={e => setForm(f => ({ ...f, notify_email: e.target.value }))}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="your@email.com"
            />
          </div>

          <div className="flex items-center gap-3">
            <button
              data-testid="toggle-enabled-btn"
              onClick={() => setForm(f => ({ ...f, is_enabled: !f.is_enabled }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${form.is_enabled ? 'bg-blue-900' : 'bg-gray-300'}`}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${form.is_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
            <span className="text-sm text-slate-700">{form.is_enabled ? 'Monitor Active' : 'Monitor Paused'}</span>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 text-sm text-gray-600 space-y-1">
            <p><strong>Schedule:</strong> Daily at 10:00 AM PST (automatic)</p>
            <p><strong>Last fetched:</strong> {settings?.last_fetched ? new Date(settings.last_fetched).toLocaleString() : 'Never'}</p>
          </div>

          <div className="flex gap-3 pt-2">
            <Button data-testid="save-settings-btn" onClick={handleSave} disabled={saving} className="bg-blue-900 hover:bg-blue-800">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
            <Button data-testid="test-email-btn" variant="outline" onClick={handleTestEmail} disabled={testing}>
              <Send size={16} className="mr-2" /> {testing ? 'Sending...' : 'Send Test Email'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// Main Admin Panel
// ═══════════════════════════════════════════════════════════════
const ADMIN_TABS = [
  { id: 'users', label: 'Users', icon: Users, group: 'admin' },
];

const FEED_TABS = [
  { id: 'feed', label: 'Bid Feeds', icon: Rss, group: 'feed' },
  { id: 'keywords', label: 'Keywords', icon: Search, group: 'feed' },
  { id: 'settings', label: 'Settings', icon: Settings, group: 'feed' },
];

const CRM_TABS = [
  { id: 'crm-dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'crm' },
  { id: 'crm-contacts', label: 'Contacts', icon: Users, group: 'crm' },
  { id: 'crm-bids', label: 'Bids', icon: Gavel, group: 'crm' },
  { id: 'crm-projects', label: 'Projects', icon: FolderKanban, group: 'crm' },
  { id: 'crm-subcontractors', label: 'Vendors / Subcontractors', icon: HardHat, group: 'crm' },
  { id: 'crm-inventory', label: 'Inventory', icon: Package, group: 'crm' },
  { id: 'crm-belzona', label: 'Belzona Estimator', icon: Calculator, group: 'crm' },
  { id: 'crm-campaigns', label: 'Campaigns', icon: Megaphone, group: 'crm' },
  { id: 'crm-analytics', label: 'Analytics', icon: BarChart3, group: 'crm' },
  { id: 'crm-email', label: 'Email Sync', icon: Inbox, group: 'crm' },
];

const AdminPanel = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isAdmin, setIsAdmin] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeTab, setActiveTab] = useState('users');
  const [activeSection, setActiveSection] = useState('admin');
  const [crmSession, setCrmSession] = useState(null);

  // Handle OAuth callback - pick up crm_session from URL
  useEffect(() => {
    const sessionId = searchParams.get('crm_session');
    if (sessionId) {
      setCrmSession(sessionId);
      localStorage.setItem('crm_session_id', sessionId);
      setSearchParams({});
      setActiveSection('crm');
      setActiveTab('crm-email');
      toast.success('Gmail connected successfully!');
    } else {
      const stored = localStorage.getItem('crm_session_id');
      if (stored) setCrmSession(stored);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    (async () => {
      try {
        const user = await apiFetch('/api/auth/me');
        if (user.email !== ADMIN_EMAIL) { setAccessDenied(true); return; }
        setIsAdmin(true);
      } catch {
        // Stale session — clear any leftover localStorage flags and send to login
        try {
          localStorage.removeItem('isAuthenticated');
          localStorage.removeItem('bcs_session_token');
        } catch (_) { /* ignore */ }
        navigate('/admin-login');
      }
    })();
  }, [navigate]);

  if (accessDenied) {
    return (
      <div className="min-h-screen bg-slate-100">
        <Header />
        <div className="pt-32 flex items-center justify-center">
          <div className="text-center max-w-md p-8 bg-white rounded-xl shadow-lg">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><XCircle className="w-8 h-8 text-red-600" /></div>
            <h2 className="text-2xl font-bold text-slate-900 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">This area is restricted to authorized administrators only.</p>
            <Button onClick={() => navigate('/')} className="bg-blue-900 hover:bg-blue-800">Return to Homepage</Button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900 mx-auto mb-4" />
          <p className="text-gray-600">Checking access...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />

      {/* Page Header */}
      <div className="pt-32 pb-8 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 to-blue-900">
        <div className="max-w-7xl mx-auto">
          <Button variant="ghost" onClick={() => navigate('/customer-portal')} className="mb-4 text-white/80 hover:text-white hover:bg-white/10">
            <ArrowLeft size={18} className="mr-2" /> Back to Dashboard
          </Button>
          <div className="flex items-center gap-3 mb-2">
            <Shield className="text-amber-400" size={32} />
            <h1 className="text-4xl font-bold text-white">Admin Panel</h1>
          </div>
          <p className="text-xl text-slate-300">Manage users, CRM, county bids, and configure alerts</p>

          {/* Section Toggle */}
          <div className="flex gap-2 mt-6">
            <button
              data-testid="section-admin"
              onClick={() => { setActiveSection('admin'); setActiveTab('users'); }}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeSection === 'admin'
                  ? 'bg-white text-slate-900 shadow-lg'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              <Shield size={14} className="inline mr-2" />Admin
            </button>
            <button
              data-testid="section-feed"
              onClick={() => { setActiveSection('feed'); setActiveTab('feed'); }}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeSection === 'feed'
                  ? 'bg-white text-slate-900 shadow-lg'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              <Rss size={14} className="inline mr-2" />Bid Feeds
            </button>
            <button
              data-testid="section-crm"
              onClick={() => { setActiveSection('crm'); setActiveTab('crm-dashboard'); }}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                activeSection === 'crm'
                  ? 'bg-white text-slate-900 shadow-lg'
                  : 'bg-white/10 text-white/80 hover:bg-white/20'
              }`}
            >
              <Briefcase size={14} className="inline mr-2" />CRM
            </button>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b bg-white sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 -mb-px overflow-x-auto" data-testid="admin-tabs">
            {(activeSection === 'admin' ? ADMIN_TABS : activeSection === 'feed' ? FEED_TABS : CRM_TABS).map(tab => (
              <button
                key={tab.id}
                data-testid={`tab-${tab.id}`}
                onClick={() => { setActiveTab(tab.id); }}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'border-blue-900 text-blue-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon size={18} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Admin Tabs */}
          {activeTab === 'users' && <UsersTab />}
          {activeTab === 'feed' && <FeedTab />}
          {activeTab === 'keywords' && <KeywordsTab />}
          {activeTab === 'settings' && <SettingsTab />}

          {/* CRM Tabs */}
          {activeTab === 'crm-dashboard' && <CRMDashboard onNavigate={setActiveTab} />}
          {activeTab === 'crm-contacts' && <CRMContacts />}
          {activeTab === 'crm-bids' && <CRMBids />}
          {activeTab === 'crm-projects' && <CRMProjects />}
          {activeTab === 'crm-subcontractors' && <CRMSubcontractors />}
          {activeTab === 'crm-inventory' && <CRMInventory />}
          {activeTab === 'crm-belzona' && <CRMBelzona />}
          {activeTab === 'crm-campaigns' && <CRMCampaigns />}
          {activeTab === 'crm-analytics' && <CRMCampaignAnalytics />}
          {activeTab === 'crm-email' && <CRMEmailSync crmSession={crmSession} />}
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AdminPanel;
